/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useLogProfilePermissions.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing log profile permissions with hierarchical inheritance.
    Uses moduleSlug (string) to match permissions with menu items.
────────────────────────────────────────────────────────────────*/

import { LogProfileFormData } from '../types';

interface MenuItem {
  id: number;
  slug?: string;
  level: number;
  parentId: number | null;
}

type PermissionType = 'logCreate' | 'logEdit' | 'logDelete' | 'notifyCreate' | 'notifyEdit' | 'notifyDelete';

export function useLogProfilePermissions(
  formData: LogProfileFormData,
  menuItems: MenuItem[],
) {
  // Get the slug for a menu item by its id
  const getSlug = (menuItemId: number): string | undefined => {
    return menuItems.find(m => m.id === menuItemId)?.slug;
  };

  const getPermissionValue = (menuItemId: number, permissionType: PermissionType): boolean => {
    const slug = getSlug(menuItemId);
    if (!slug) return false;
    const permission = formData.permissions.find((p) => p.moduleSlug === slug);
    if (permission) {
      return permission[permissionType];
    }
    return false;
  };

  // Helper to check if a menu item is a "leaf" (level 3 OR level 2 without level 3 children)
  const isLeafItem = (itemId: number): boolean => {
    const item = menuItems.find(m => m.id === itemId);
    if (!item) return false;
    if (item.level === 3) return true;
    if (item.level === 2) {
      const hasLevel3Children = menuItems.some(
        child => child.parentId === itemId && child.level === 3
      );
      return !hasLevel3Children;
    }
    return false;
  };

  const allChildrenHavePermission = (parentId: number, permissionType: PermissionType): boolean => {
    const directChildren = menuItems.filter((item) => item.parentId === parentId);
    if (directChildren.length === 0) return false;

    return directChildren.every((child) => {
      // If child is a leaf item (level 3 or level 2 without children), check its direct permission
      if (isLeafItem(child.id)) {
        return getPermissionValue(child.id, permissionType);
      }
      // Otherwise, recurse into child's children
      return allChildrenHavePermission(child.id, permissionType);
    });
  };

  const someChildrenHavePermission = (parentId: number, permissionType: PermissionType): boolean => {
    const directChildren = menuItems.filter((item) => item.parentId === parentId);
    if (directChildren.length === 0) return false;

    return directChildren.some((child) => {
      // If child is a leaf item (level 3 or level 2 without children), check its direct permission
      if (isLeafItem(child.id)) {
        return getPermissionValue(child.id, permissionType);
      }
      // Otherwise, recurse into child's children
      return someChildrenHavePermission(child.id, permissionType);
    });
  };

  const isPermissionInherited = (menuItemId: number, permissionType: PermissionType): boolean => {
    const menuItem = menuItems.find((item) => item.id === menuItemId);
    if (!menuItem) return false;

    // First check if this item has the permission directly
    const directPermission = getPermissionValue(menuItemId, permissionType);
    if (directPermission) {
      return false; // It's a direct permission, not inherited
    }

    // Ascending loop: check all ancestors
    let currentParentId = menuItem.parentId;
    while (currentParentId) {
      const parent = menuItems.find((item) => item.id === currentParentId);
      if (!parent) break;

      // Check if this parent has the permission (calculated from its children)
      const parentHasPermission = allChildrenHavePermission(parent.id, permissionType);
      if (parentHasPermission) {
        return true;
      }

      // Move up to the next parent
      currentParentId = parent.parentId;
    }

    return false;
  };

  const handlePermissionChange = (
    menuItemId: number,
    permissionType: PermissionType,
    value: boolean,
    setFormData: (updater: (prev: LogProfileFormData) => LogProfileFormData) => void,
  ) => {
    const menuItem = menuItems.find((item) => item.id === menuItemId);
    if (!menuItem) {
      return;
    }

    // Get all leaf item slugs that should be updated
    const slugsToUpdate: string[] = [];

    if (menuItem.level === 1) {
      // If it's a section, get all final items in all groups
      const groups = menuItems.filter((item) => item.parentId === menuItemId);
      groups.forEach((group) => {
        const groupItems = menuItems.filter((item) => item.parentId === group.id && item.level === 3);
        if (groupItems.length > 0) {
          // Has level 3 children - update them
          groupItems.forEach((item) => { if (item.slug) slugsToUpdate.push(item.slug); });
        } else {
          // No level 3 children - treat level 2 as final item (e.g., Apps menu)
          if (group.slug) slugsToUpdate.push(group.slug);
        }
      });
    } else if (menuItem.level === 2) {
      // If it's a group, get all final items in this group
      const groupItems = menuItems.filter((item) => item.parentId === menuItemId && item.level === 3);
      if (groupItems.length > 0) {
        // Has level 3 children - update them
        groupItems.forEach((item) => { if (item.slug) slugsToUpdate.push(item.slug); });
      } else {
        // No level 3 children - treat this level 2 item as final item
        if (menuItem.slug) slugsToUpdate.push(menuItem.slug);
      }
    } else if (menuItem.level === 3) {
      // If it's already a final item, just update itself
      if (menuItem.slug) slugsToUpdate.push(menuItem.slug);
    }

    setFormData((prev) => {
      const newPermissions = [...prev.permissions];

      // Update all slugs
      slugsToUpdate.forEach((slug) => {
        let existingPermission = newPermissions.find((p) => p.moduleSlug === slug);
        if (!existingPermission) {
          existingPermission = {
            moduleSlug: slug,
            logCreate: false,
            logEdit: false,
            logDelete: false,
            notifyCreate: false,
            notifyEdit: false,
            notifyDelete: false,
          };
          newPermissions.push(existingPermission);
        }

        existingPermission[permissionType] = value;
      });

      return {
        ...prev,
        permissions: newPermissions,
      };
    });
  };

  return {
    getPermissionValue,
    allChildrenHavePermission,
    someChildrenHavePermission,
    isPermissionInherited,
    isLeafItem,
    handlePermissionChange,
  };
}
